import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Delete, Check, X, ArrowLeft, Camera, AlertCircle, CreditCard, Wallet, TriangleAlert } from 'lucide-react'
// #1150: html5-qrcode は main bundle 最大の重り (minify 前 ~1.2MB)。ページ自体は
// 決済動線なので eager のまま、ライブラリだけカメラ起動時に dynamic import する
// (カメラ許可ダイアログ〜起動の間に並行ロードされるため体感差はない)。
import type { Html5Qrcode } from 'html5-qrcode'
import { useStore, FEE_CONFIG } from '../store/useStore'
import type { Store } from '../store/useStore'
import ProcessingOverlay from '../components/ProcessingOverlay'
import StripeCheckout from '../components/StripeCheckout'
import { useI18n } from '../i18n/useI18n'
import { classifyCameraError, cameraErrorKeys, type CameraFailure } from '../lib/cameraError'
import { formatHomeCurrency } from '../utils/currency'
import { showToast } from '../components/Toast'
import { api, ApiError } from '../services/api'
import { isWeb3Mode } from '../lib/authMode'
import { storePaymentOptions } from '../lib/storePayment'
import { paymentDeclineI18nKey } from '../lib/apiErrorMessage'
import { hasWeb3Provider } from '../lib/ethers'
import { useEnrollmentGate, markEnrolled } from '../hooks/useEnrollmentGate'
import PasskeyEnrollmentModal from '../components/PasskeyEnrollmentModal'
import AutoTopUpSheet from '../components/AutoTopUpSheet'
import { useAutoTopUp, calculateAutoTopupCardFee } from '../hooks/useAutoTopUp'
import { PAYMENT_METHOD_POINTS } from '../lib/paymentMethods'

const QR_READER_ID = 'yokoso-qr-reader'

type ScanLocationState = {
  preScannedStoreId?: string
  storeId?: string
  /**
   * #1987 何を読みに来たか。**スタンプラリーは支払いではない。**
   *
   * この画面は支払いの QR もスタンプの QR も読めるが、見出しは
   * 「スキャンして支払う」で固定だった。スタンプを押すだけの操作で「支払う」と
   * 書いてあると、お金が引かれると思って読み取りをためらう人が出る
   * (イベント会場に説明する人が常にいるとは限らない)。
   */
  purpose?: 'stamp'
} | null

export default function QRScan() {
  const navigate = useNavigate()
  const location = useLocation()
  const { wallet, pay, payWithStripe, currentStore, stores } = useStore()
  const { t, locale } = useI18n()

  // #1987 遷移元が渡す用途 (スタンプラリーからか、支払いからか)
  const scanPurpose = (location.state as ScanLocationState)?.purpose
  const [phase, setPhase] = useState<'camera' | 'recognized' | 'sheet'>('camera')
  const [amount, setAmount] = useState('')
  const [showProcessing, setShowProcessing] = useState(false)
  const [scannedStore, setScannedStore] = useState<Store | null>(null)
  const [cameraError, setCameraError] = useState<string | null>(null)
  // #2111 カメラが開けない理由。「起動できません」だけだと、許可を拒否したのか
  // 端末の問題なのかが本人にも分からず、直しようがなかった (当日に発生)
  const [cameraFailure, setCameraFailure] = useState<CameraFailure | null>(null)
  // 許可し直した後に押してもらう「もう一度試す」。値を変えると起動の effect が走る
  const [cameraRetry, setCameraRetry] = useState(0)
  const [showPaymentChoice, setShowPaymentChoice] = useState(false)
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null)
  const [stripeConnectedAccountId, setStripeConnectedAccountId] = useState<string | null>(null)
  const [stripePaymentIntentId, setStripePaymentIntentId] = useState<string | null>(null)
  const [stripeLoading, setStripeLoading] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  // 決済は成立したが記録を確認できなかった状態。「もう一度試す」を出さない (二重払い防止)
  const [paymentUnconfirmed, setPaymentUnconfirmed] = useState(false)
  // #734: Passkey 未登録ユーザーの決済 trigger 時に enrollment modal で誘導
  const { requiresEnrollment } = useEnrollmentGate()
  const [passkeyModalOpen, setPasskeyModalOpen] = useState(false)
  const payPromiseRef = useRef<Promise<void> | null>(null)
  const scannerRef = useRef<Html5Qrcode | null>(null)
  // #989: Stripe(Link) を一度でも表示したら true。次回のパスキー認証の前に
  // Link の裏 passkey(conditional WebAuthn)を確実に破棄するための待機要否判定。
  const stripeSheetShownRef = useRef(false)
  // #1038 「この支払い試行」の id。同じ試行の retry では同じ値を送って
  // PaymentIntent の二重生成を防ぐ。**支払いが成立したら振り直す** —
  // 同じ店で同じ額をもう一度払う (2 杯目) のは正常な操作で、そこまで畳むと
  // 客が 2 回払ったつもりで 1 回しか課金されない。
  const payAttemptIdRef = useRef<string | null>(null)
  const isScanningRef = useRef(false)

  const hasStripe = !!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY

  const balance = wallet?.balanceJPYC ?? 0
  const numericAmount = parseInt(amount || '0', 10)
  const balanceAfter = balance - numericAmount
  const isInsufficient = numericAmount > balance

  const targetStore = scannedStore || currentStore || stores[0]

  // #1038 金額や店が変わったら支払い試行は別物。id を振り直す。
  // Stripe は**同じ idempotency key に違うパラメータ**が来るとエラーを返すので、
  // 振り直さないと「金額を打ち直して払う」が決済エラーになる。
  useEffect(() => {
    payAttemptIdRef.current = null
  }, [numericAmount, targetStore?.id])
  const storeName = targetStore?.name || '---'

  // Determine if this will be an on-chain payment
  const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true'
  const isOnChain = !isDemoMode && isWeb3Mode() && hasWeb3Provider() && !!targetStore?.walletAddress

  // Processing steps vary by mode
  const processingSteps = useMemo(() => {
    if (isOnChain) {
      return [t('scan.signingTx'), t('scan.broadcastingTx'), t('scan.confirmingTx')]
    }
    return [t('scan.processing')]
  }, [isOnChain, t])

  const processingDuration = isOnChain ? 5000 : 1500

  // ── Handle a decoded QR code ──
  const handleQRDetected = useCallback((decodedText: string) => {
    // Accepted formats:
    //   - https://.../pay/{id}      → payment flow
    //   - https://.../stores/{id}   → open menu page
    //   - https://.../stamp?t=xxx   → stamp rally (#1126) — StampGate に委譲
    let kind: 'pay' | 'menu' | 'stamp' | null = null
    let storeId: string | null = null
    let stampToken: string | null = null

    try {
      const url = new URL(decodedText)
      const payMatch = url.pathname.match(/^\/pay\/([^/]+)\/?$/)
      const menuMatch = url.pathname.match(/^\/stores\/([^/]+)\/?$/)
      // /stamp は完全一致 (末尾スラッシュのみ許容) — /stamp-rally を巻き込まない
      const isStampPath = url.pathname.replace(/\/$/, '') === '/stamp'
      if (payMatch) {
        kind = 'pay'
        storeId = payMatch[1]
      } else if (menuMatch) {
        kind = 'menu'
        storeId = menuMatch[1]
      } else if (isStampPath && url.searchParams.get('t')) {
        kind = 'stamp'
        stampToken = url.searchParams.get('t')
      }
    } catch {
      // Not a URL — fall through to unrecognized
    }

    if (!kind || (kind !== 'stamp' && !storeId)) {
      console.log('[QR] Unrecognized format:', decodedText)
      return
    }

    // Stop scanning
    if (scannerRef.current && isScanningRef.current) {
      scannerRef.current.stop().catch(() => {})
      isScanningRef.current = false
    }

    if (kind === 'stamp') {
      // 決済シートには入らず StampGate に収束 (外部カメラ経路と同一ハンドラ)
      navigate(`/stamp?t=${encodeURIComponent(stampToken!)}`)
      return
    }
    if (!storeId) return // stamp 以外は storeId 必須 (上のガードで実質保証済み)

    if (kind === 'menu') {
      navigate(`/stores/${storeId}`)
      return
    }

    // Resolve store: prefer in-memory list, fall back to API fetch because
    // the stores array may be empty when the user arrived via /pay/:storeId
    // without visiting the Home / NearbyStores pages first.
    const localStore = stores.find(s => s.id === storeId)
    const targetId = storeId
    if (localStore) {
      setScannedStore(localStore)
      setPhase('recognized')
      setTimeout(() => setPhase('sheet'), 800)
      return
    }
    api.getStore(targetId)
      .then((fetched) => {
        if (!fetched) {
          showToast({ type: 'error', message: t('scan.storeNotFound') })
          return
        }
        setScannedStore(fetched as Store)
        setPhase('recognized')
        setTimeout(() => setPhase('sheet'), 800)
      })
      .catch(() => {
        showToast({ type: 'error', message: t('scan.storeNotFound') })
      })
  }, [stores, t, navigate])

  // ── Pre-scanned store from /pay/:storeId route ──
  useEffect(() => {
    const state = location.state as ScanLocationState
    // StoreDetail の「この店舗で支払う」は storeId で渡してくる (Pay.tsx は preScannedStoreId)
    const preScannedStoreId = state?.preScannedStoreId ?? state?.storeId
    if (!preScannedStoreId) return

    // Pay.tsx already resolved + setCurrentStore before navigating here, so
    // prefer currentStore. Fall back to the in-memory stores list for safety.
    const resolved =
      (currentStore && currentStore.id === preScannedStoreId ? currentStore : null) ||
      stores.find(s => s.id === preScannedStoreId) ||
      null
    if (!resolved) {
      showToast({ type: 'error', message: t('scan.storeNotFound') })
      return
    }
    setScannedStore(resolved)
    setPhase('sheet')
    // Clear state so going back to /scan doesn't re-trigger
    navigate(location.pathname, { replace: true, state: null })
  }, [location.state, location.pathname, navigate, stores, currentStore, t])

  // ── Start camera & QR scanner ──
  useEffect(() => {
    if (phase !== 'camera') return
    if (isScanningRef.current) return

    let cancelled = false

    const startCamera = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode')
        if (cancelled) return
        const scanner = new Html5Qrcode(QR_READER_ID)
        scannerRef.current = scanner

        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 220, height: 220 },
            aspectRatio: 1.0,
          },
          (decodedText) => {
            if (!cancelled) handleQRDetected(decodedText)
          },
          () => {} // scan-frame errors (expected, ignore)
        )

        if (!cancelled) {
          isScanningRef.current = true
          setCameraError(null)
        } else {
          // Component unmounted before start resolved
          scanner.stop().catch(() => {})
        }
      } catch (err: unknown) {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : String(err)
        console.warn('[QR] Camera start failed:', msg)
        setCameraError(msg)
        setCameraFailure(classifyCameraError(err))
      }
    }

    // Small delay to ensure the DOM element is rendered
    const timer = setTimeout(startCamera, 300)

    return () => {
      cancelled = true
      clearTimeout(timer)
      if (scannerRef.current && isScanningRef.current) {
        scannerRef.current.stop().catch(() => {})
        isScanningRef.current = false
      }
    }
  }, [phase, handleQRDetected, cameraRetry])

  // ── Restart camera when sheet is closed ──
  const handleCloseSheet = () => {
    setPhase('camera')
    setAmount('')
    setScannedStore(null)
  }

  const handleNumpad = (key: string) => {
    if (key === 'delete') {
      setAmount((prev) => prev.slice(0, -1))
    } else {
      if (amount.length < 7) {
        setAmount((prev) => prev + key)
      }
    }
  }

  // #1190 Phase 4: YOKOSO 通貨支払を明示的な支払手段として常時表示するかの gate。
  // demo mode (or Web3 mode) の時のみ true。isYokosoPointsEnabled 内部で isWeb3Mode
  // に fallback するので、Web3 mode ユーザーは引き続き yokoso option を見られる。
  //
  // #1438 店がポイントを受けられない場合はここで落とす。理由は 2 つ:
  //
  //   1. 店が断っている (acceptsPoints)。**未設定は「受ける」** —
  //      既定を false にすると今受けられている店で静かに壊れる
  //   2. **KYC 未完了 (現金のみ店 = #1431)**。弊社発行ポイントは店の Connect
  //      アカウントへ送金して初めて店にお金が渡るので、Connect が無い店では
  //      客の残高だけ減って店に 1 円も入らない
  //
  // サーバ側 (`/payment/execute`) でも同じ判定をする — 画面で隠すのは境界ではなく、
  // **レジで選ばせてから断るのが一番悪い体験**なので手前で消しておくためのもの。
  //
  //   「現金のみ運用」かどうかでは判定しない。KYC 済みの店が (カード手数料を避けて)
  //   現金のみ運用にしていても、Stripe の口座はあるのでポイントは受け取れる。
  //   `paymentEnabled` は backend が owner の KYC (chargesEnabled) から付ける値。
  // 判定表は lib/storePayment.ts (StoreDetail と共通)。カードは KYC 済みで「現金のみ運用」
  // でない店だけ — 現金のみの店にカードを出すと create-payment-intent で落ちてから断ることになる。
  const { cardInApp: cardInAppAllowed, points: yokosoEnabled } = storePaymentOptions(targetStore)
  // カードもポイントも使えない店 (KYC 未完了 / KYC 済みでポイントを断っている現金のみの店 /
  // アプリ側でポイント払いを出していない環境の現金のみの店)。金額を入れてから断らず、
  // シートの時点で「アプリ内では払えない」と案内して止める。
  const noInAppPayment = !cardInAppAllowed && !yokosoEnabled
  const { executeWithTopup } = useAutoTopUp()
  const [showAutoTopUp, setShowAutoTopUp] = useState(false)

  const handleConfirm = () => {
    if (numericAmount <= 0 || showProcessing) return
    if (noInAppPayment) return // シートで案内済み・ボタンも無効
    // #1190 Phase 4: YOKOSO 通貨表示 mode では method picker を出して客に選ばせる。
    // #1769 現金のみ運用 (KYC 済み) の店はカードを出さず、下のポイント払いに落ちる。
    if (yokosoEnabled && hasStripe && cardInAppAllowed) {
      setShowPaymentChoice(true)
      return
    }
    // Non-demo default: Stripe に直行 (従来挙動)
    if (hasStripe && cardInAppAllowed) {
      handlePayWithStripe()
      return
    }
    // Stripe 未設定 → JPYC balance に落ちる (従来挙動)
    if (isInsufficient) return
    handlePayWithBalance()
  }

  const handlePayWithBalance = () => {
    if (numericAmount <= 0 || showProcessing) return
    // #1190 Phase 4: 残高不足時、demo mode なら auto top-up sheet を出して、
    // 客に「不足分チャージ + 全額支払」を 1 tap で承認させる。
    if (isInsufficient) {
      if (yokosoEnabled) {
        setShowPaymentChoice(false)
        setShowAutoTopUp(true)
      }
      return
    }
    setShowPaymentChoice(false)
    setShowProcessing(true)
    setPhase('camera')
    payPromiseRef.current = pay(numericAmount, targetStore)
  }

  // #1190 Phase 4: auto top-up 承認時。1 API call で charge + payment を原子的に完了。
  const handleAutoTopUpConfirm = async () => {
    if (!targetStore || numericAmount <= 0) return
    const topupNeeded = Math.max(0, numericAmount - balance)
    const topupFee = calculateAutoTopupCardFee(topupNeeded)
    setShowAutoTopUp(false)
    setShowProcessing(true)
    setPhase('camera')
    try {
      const topupRes = await executeWithTopup({
        storeId: targetStore.id,
        billAmount: numericAmount,
        topupAmount: topupNeeded,
        topupFee,
      })
      // PaymentComplete 画面が使う lastPayment* を store にセット
      // (通常 pay() の中で行われるが、今回は auto-topup 経由なので手動)。
      useStore.setState({
        lastPaymentAmount: numericAmount,
        lastPaymentFee: 0,
        lastPaymentTxHash: null,
        lastPaymentExplorerLink: null,
        lastPaymentMethod: PAYMENT_METHOD_POINTS,
        // #1397 レシート導線が掴む取引 ID。charge ではなく **payment** の方。
        lastPaymentTxId: (topupRes?.payment_transaction_id as string) || null,
        lastPaymentOrderIds: [],
        lastTxTimestamp: Date.now(),
        currentStore: targetStore,
      })
      navigate('/payment/complete', { replace: true })
    } catch (e) {
      setPaymentError(e instanceof Error ? e.message : String(e))
      setShowProcessing(false)
    }
  }

  // #392 / #621: 決済上限違反のエラーコードを i18n メッセージにマップ。
  // 未知のエラー (ApiError 以外 / code 無し) は汎用メッセージにフォールバック。
  // 上限 / 現金のみ運用 / ポイント不可 の理由は lib/apiErrorMessage.ts の対応表 (#1801 で集約)
  const resolvePaymentErrorMessage = (err: unknown): string =>
    t(paymentDeclineI18nKey(err) ?? 'scan.paymentFailed')

  const handlePayWithStripe = async () => {
    if (numericAmount <= 0 || showProcessing) return

    // #734: 決済 trigger 直前に enrollment 必須チェック (= UX 改善)。
    // 未登録なら決済画面に行く前に Passkey 登録 modal を直接出す
    // (= 「決済エラー」の generic 表示で混乱させない)。
    if (requiresEnrollment) {
      setPasskeyModalOpen(true)
      return
    }

    setStripeLoading(true)
    // #989: Link の裏 passkey(conditional WebAuthn)と決済前パスキー認証が iOS で
    // 競合(同時に2つの credentials.get は不可)し「2回目以降ずっと失敗」する。
    // 前回 Stripe シート(=Link)を出していたら、まず Elements をアンマウントして
    // Stripe 側の Link ceremony を破棄 → 少し待ってからこちらのパスキー認証を始める
    // (= パスキー確認が取れてから Link を動かす厳密な順序付け)。
    if (stripeSheetShownRef.current) {
      setStripeClientSecret(null)
      setStripeConnectedAccountId(null)
      setStripePaymentIntentId(null)
      await new Promise((resolve) => setTimeout(resolve, 500))
    }
    // #987 fix: 登録済み(passkey あり)かどうかを catch で参照するため try の外で保持。
    let hadPasskey = false
    try {
      const userId = localStorage.getItem('userId') || 'demo_user_001'

      // #734: enrollment_mode が required_* / optional (credentials あり) なら
      // 決済前に Passkey 認証 → authLogId を assertion として送信。
      // disabled / 失敗ケースは undefined で送り、backend が mode に応じて判定。
      let assertionId: string | undefined
      try {
        const cfg = await api.getWebauthnConfig()
        if (cfg.enrollmentMode !== 'disabled') {
          const creds = await api.listWebauthnCredentials().catch(() => ({ credentials: [] }))
          const hasCreds = (creds.credentials || []).length > 0
          hadPasskey = hasCreds
          if (cfg.enrollmentMode === 'required_all' || hasCreds) {
            const auth = await api.webauthnAuthBegin()
            const { startAuthentication } = await import('@simplewebauthn/browser')
            const credential = await startAuthentication({
              optionsJSON: auth.options as Parameters<typeof startAuthentication>[0]['optionsJSON'],
            })
            const r = await api.webauthnAuthComplete(auth.challengeId, credential)
            assertionId = r.authLogId
          }
        }
      } catch (e) {
        // Passkey 認証失敗 → backend で 401 を期待 (= 適切なエラー UI)。
        // required_* mode なら createStripePaymentIntent が 401 を返す。
        console.warn('[QRScan] webauthn pre-payment auth failed:', e)
      }

      // 無ければ採番、あれば使い回す (= 同じ試行の retry を畳む)。
      payAttemptIdRef.current ||= crypto.randomUUID()
      const result = await api.createStripePaymentIntent(
        targetStore.id, numericAmount, userId, assertionId, payAttemptIdRef.current)
      setStripeClientSecret(result.client_secret)
      stripeSheetShownRef.current = true
      setStripeConnectedAccountId(result.connected_account_id || null)
      setStripePaymentIntentId(result.payment_intent_id || null)
      setShowPaymentChoice(false)
    } catch (err) {
      // #734: 401 + WebAuthn 関連 code → Passkey enrollment modal を出す
      // (= cache stale / 別ブラウザで credentials 削除 等のレアケースもカバー)
      if (
        err instanceof ApiError &&
        err.status === 401 &&
        (err.code === 'WEBAUTHN_REQUIRED' || err.code === 'WEBAUTHN_INVALID')
      ) {
        // #987 fix: 登録済みユーザーの本人認証が失敗した時に「登録モーダル」を出すと
        // 「登録済みなのに登録を促される＋全画面ブロック」になる。登録済みなら登録では
        // なく再認証(エラー)を表示し、再度「支払う」でリトライできるようにする。
        if (hadPasskey) {
          setPaymentError(t('payment.passkeyAuthRetry'))
        } else {
          setPasskeyModalOpen(true)
        }
        return
      }
      // #1227: 決済 = 端末信頼 + UV。未信頼端末は DEVICE_NOT_TRUSTED。
      // 端末信頼の復元 (TOTP 再ログイン / 既存端末の承認) へ誘導する。
      if (
        err instanceof ApiError &&
        err.status === 401 &&
        err.code === 'DEVICE_NOT_TRUSTED'
      ) {
        setPaymentError(t('payment.deviceNotTrusted'))
        return
      }
      setPaymentError(resolvePaymentErrorMessage(err))
    } finally {
      setStripeLoading(false)
    }
  }

  const handleStripeSuccess = async (paymentIntentId: string) => {
    try {
      await payWithStripe(numericAmount, targetStore, paymentIntentId)
      // #1038 支払いが成立したのでこの試行は終わり。次の支払いは別 PaymentIntent に
      // なるよう振り直す (振り直さないと「もう一杯」が課金されない)。
      payAttemptIdRef.current = null
      setStripeClientSecret(null)
      setStripePaymentIntentId(null)
      navigate('/payment/complete')
    } catch (err) {
      // ここに来た時点で Stripe は succeeded を返している (記録 API の失敗)。
      // - 通信断 / 5xx: 決済は成立していて webhook が後から記録する (#224)。
      //   「失敗、もう一度」と出すと二重払いを招くので、再試行させない
      // - 4xx: backend が理由付きで断った (上限違反は自動返金 / 検証失敗)。
      //   成立したとは言えないので従来どおりの失敗表示
      if (!(err instanceof ApiError) || err.status >= 500) {
        setPaymentUnconfirmed(true)
        setPaymentError(t('scan.paymentUnconfirmed'))
      } else {
        setPaymentError(resolvePaymentErrorMessage(err))
      }
    }
  }

  const handleStripeCancelOrBack = () => {
    setStripeClientSecret(null)
    setStripePaymentIntentId(null)
    setShowPaymentChoice(false)
  }

  const handleProcessingComplete = async () => {
    try {
      await payPromiseRef.current
      navigate('/payment/complete')
    } catch {
      setShowProcessing(false)
      setAmount('')
      setPaymentError(t('scan.paymentFailed'))
    }
  }

  const numpadKeys = [
    '1', '2', '3',
    '4', '5', '6',
    '7', '8', '9',
    'delete', '0', 'confirm',
  ]

  const isRecognized = phase === 'recognized' || phase === 'sheet'

  if (paymentError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-warm-white px-6 safe-top safe-bottom">
        <div className="w-full max-w-sm text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <TriangleAlert size={32} className="text-red-500" />
          </div>
          <h2 className="mt-4 font-display text-lg font-bold text-text-primary">
            {t(paymentUnconfirmed ? 'scan.paymentUnconfirmedTitle' : 'scan.errorTitle')}
          </h2>
          <p className="mt-3 font-body text-sm text-text-secondary leading-relaxed">
            {paymentError}
          </p>
          <div className="mt-6 space-y-3">
            {paymentUnconfirmed ? (
              <button
                onClick={() => navigate('/history')}
                className="w-full rounded-xl bg-navy py-3 text-sm font-bold text-white"
              >
                {t('myOrders.seeHistory')}
              </button>
            ) : (
              <button
                onClick={() => { setPaymentError(null); setStripeClientSecret(null) }}
                className="w-full rounded-xl bg-navy py-3 text-sm font-bold text-white"
              >
                {t('scan.retry')}
              </button>
            )}
            <button
              onClick={() => navigate(-1)}
              className="w-full rounded-xl border border-gray-200 py-3 text-sm font-medium text-text-secondary"
            >
              {t('scan.goBack')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen bg-black/95">
      {/* Processing Overlay */}
      <ProcessingOverlay
        visible={showProcessing}
        steps={processingSteps}
        stepDuration={processingDuration}
        onComplete={handleProcessingComplete}
      />

      {/* Header */}
      <div className="safe-top relative z-50 flex items-center justify-between px-4 pt-6 pb-4">
        <button onClick={() => navigate(-1)} className="tap-effect p-2 -ml-2">
          <ArrowLeft size={22} className="text-white" />
        </button>
        <h1 className="font-display text-lg font-semibold text-white">
          {/* #1987 支払い経路は従来どおり「スキャンして支払う」。何をする画面かが
            * 分かる方がよいので、全部を中立の文言にはしない。 */}
          {t(scanPurpose === 'stamp' ? 'scan.titleStamp' : 'scan.title')}
        </h1>
        <div className="w-[38px]" />
      </div>

      {/* Camera View Area */}
      <div className="flex flex-col items-center px-8 pt-4">
        <div className="relative w-full max-w-[280px] aspect-square rounded-2xl overflow-hidden bg-gray-900">
          {/* html5-qrcode mounts the video here.
            *
            * #1988 **枠は正方形、カメラ映像は 4:3 や 16:9。** 比率が合わないので
            * 何もしないと枠の中で映像が寄って見える (四隅のブラケットは枠基準に
            * 置いてあるので、映像だけがズレて見える)。
            * html5-qrcode は video にインラインの width/height を付けるため、
            * それを上書きできる形で中央を基準に埋める。 */}
          <style>{`
            /* **ライブラリが器にインラインの position:relative を書き込む。**
             * インラインは Tailwind の absolute inset-0 に勝つので、器は映像の
             * 高さ (280x210) まで縮み、正方形の枠の下に隙間が残る — これが
             * 報告された「映像が枠からズレる」の正体。器を枠いっぱいに戻す。 */
            #${QR_READER_ID} {
              position: absolute !important;
              inset: 0;
              line-height: 0;
            }
            /* video にも width/height がインラインで付くので !important で上書きし、
             * はみ出す分は切り取る (引き伸ばして歪めない)。 */
            #${QR_READER_ID} video {
              width: 100% !important;
              height: 100% !important;
              object-fit: cover;
              display: block;
            }
            /* 器が枠いっぱいになるとライブラリ側の枠 (暗い縁取りと緑のブラケット) が
             * 出てくる。**この画面は白いブラケットと走査線を自前で描いている**ので、
             * 二重になって見づらい。読み取り範囲は qrbox の指定どおりで変わらない。 */
            #qr-shaded-region { display: none !important; }
          `}</style>
          <div
            id={QR_READER_ID}
            className="absolute inset-0"
            style={{ display: phase === 'camera' && !cameraError ? 'block' : 'none' }}
          />

          {/* Camera error / fallback */}
          {cameraError && phase === 'camera' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-900 px-4">
              <AlertCircle size={32} className="text-white/40" />
              {/* #2111 **理由と、やることを出す。** 以前は理由を問わず
                * 「カメラを起動できません」の 1 文だけで、許可を拒否したことに
                * 本人が気付けなかった。 */}
              <p className="text-center font-body text-sm font-medium text-white/80">
                {t(cameraErrorKeys(cameraFailure ?? 'unknown').title)}
              </p>
              <p className="text-center font-body text-xs text-white/50 leading-relaxed">
                {t(cameraErrorKeys(cameraFailure ?? 'unknown').hint)}
              </p>
              <button
                onClick={() => {
                  setCameraError(null)
                  setCameraFailure(null)
                  setCameraRetry((n) => n + 1)
                }}
                className="tap-effect mt-1 rounded-lg border border-white/30 px-5 py-2 font-body text-sm text-white"
              >
                {t('scan.cameraRetry')}
              </button>
            </div>
          )}

          {/* Scan frame overlay (shown over camera feed) */}
          {phase === 'camera' && !cameraError && (
            <div className="pointer-events-none absolute inset-0 z-10">
              {/* Corner brackets */}
              <div className="absolute top-[20%] left-[15%] h-5 w-5 border-t-2 border-l-2 border-white rounded-tl-sm" />
              <div className="absolute top-[20%] right-[15%] h-5 w-5 border-t-2 border-r-2 border-white rounded-tr-sm" />
              <div className="absolute bottom-[20%] left-[15%] h-5 w-5 border-b-2 border-l-2 border-white rounded-bl-sm" />
              <div className="absolute bottom-[20%] right-[15%] h-5 w-5 border-b-2 border-r-2 border-white rounded-br-sm" />

              {/* Scan line */}
              <div
                className="absolute left-[15%] right-[15%] h-0.5 bg-matcha"
                style={{ animation: 'scan-line 2s ease-in-out infinite', top: '40%' }}
              />
            </div>
          )}

          {/* Recognized store overlay */}
          {isRecognized && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-black/60">
              <div className="animate-scale-in rounded-xl bg-black/70 px-6 py-3">
                <p className="font-jp text-lg font-bold text-matcha">{storeName}</p>
              </div>
            </div>
          )}
        </div>

        {/* Instruction text */}
        <p className="mt-4 font-body text-sm text-white/70">
          {phase === 'camera'
            ? cameraError
              ? t(cameraErrorKeys(cameraFailure ?? 'unknown').title)
              : t('scan.instruction')
            : t('scan.recognized')}
        </p>
      </div>

      {/* #2111 **スタンプラリーから来た人に残高を見せない。** QR を読むだけの人に
        * 「あなたの残高 ¥0」が出ると、お金が要る仕組みだと思わせてしまう
        * (イベントで実際に驚かれた)。支払いの経路では、払う前に残高が要るので
        * 従来どおり出す。 */}
      {scanPurpose !== 'stamp' && (
        <>
          {/* Divider */}
          <div className="mx-8 mt-6 border-t border-white/20" />

          {/* Balance */}
          <div className="mt-4 flex flex-col items-center">
            <p className="font-body text-sm text-white/60">{t('scan.yourBalance')}</p>
            <p className="mt-1 font-display text-2xl font-bold text-white">
              &yen; {balance.toLocaleString()}
            </p>
            {formatHomeCurrency(balance, locale) && (
              <p className="mt-0.5 font-body text-xs text-white/50">
                {formatHomeCurrency(balance, locale)}
              </p>
            )}
          </div>
        </>
      )}

      {/* Camera icon badge (indicates live camera) */}
      {phase === 'camera' && !cameraError && (
        <div className="mt-5 flex justify-center">
          <div className="flex items-center gap-2 rounded-xl border border-matcha/40 bg-matcha/10 px-4 py-2">
            <Camera size={16} className="text-matcha" />
            <span className="font-body text-xs text-matcha font-medium">{t('scan.cameraActive')}</span>
          </div>
        </div>
      )}

      {/* Bottom Sheet — full-screen overlay so it's always on top */}
      {phase === 'sheet' && (
        <>
        <div className="fixed inset-0 z-[100] flex flex-col bg-black/80" style={{ maxWidth: 430, margin: '0 auto' }}>
          {/* Top spacer (tap to close) */}
          <div className="flex-shrink-0 h-16" onClick={handleCloseSheet} />
          {/* Sheet */}
          <div className="animate-slide-up flex-1 rounded-t-3xl bg-white shadow-2xl overflow-auto">
          {/* Header with close button */}
          <div className="flex items-center justify-between px-5 pt-4 pb-2">
            <div className="w-8" />
            <div className="h-1 w-10 rounded-full bg-gray-300" />
            <button onClick={handleCloseSheet} className="tap-effect p-1">
              <X size={20} className="text-text-tertiary" />
            </button>
          </div>

          {/* Content */}
          <div className="px-5 pb-6">
            {/* Pay to heading */}
            <h2 className="text-center font-display text-lg font-bold text-text-primary">
              {t('scan.payTo', { store: storeName })}
            </h2>

            {/* Amount display */}
            <div className="mt-4 flex items-center justify-center">
              <p className="font-display text-3xl font-bold text-text-primary">
                &yen; {numericAmount > 0 ? numericAmount.toLocaleString() : '0'}
              </p>
            </div>

            {/* Numpad */}
            <div className="mt-4 grid grid-cols-3 gap-2">
              {numpadKeys.map((key) => {
                if (key === 'delete') {
                  return (
                    <button
                      key={key}
                      onClick={() => handleNumpad('delete')}
                      className="tap-effect flex h-[52px] items-center justify-center rounded-xl bg-gray-50 transition-colors hover:bg-gray-100"
                    >
                      <Delete size={22} className="text-text-primary" />
                    </button>
                  )
                }
                if (key === 'confirm') {
                  return (
                    <button
                      key={key}
                      onClick={handleConfirm}
                      disabled={numericAmount <= 0 || (yokosoEnabled && !hasStripe && isInsufficient) || showProcessing || noInAppPayment}
                      className={`tap-effect flex h-[52px] items-center justify-center rounded-xl transition-colors ${
                        numericAmount > 0 && !(yokosoEnabled && !hasStripe && isInsufficient) && !showProcessing && !noInAppPayment
                          ? 'bg-matcha hover:bg-matcha/90'
                          : 'bg-gray-200 cursor-not-allowed'
                      }`}
                    >
                      <Check size={22} className="text-white" />
                    </button>
                  )
                }
                return (
                  <button
                    key={key}
                    onClick={() => handleNumpad(key)}
                    className="tap-effect flex h-[52px] items-center justify-center rounded-xl bg-gray-50 font-display text-xl text-text-primary transition-colors hover:bg-gray-100"
                  >
                    {key}
                  </button>
                )
              })}
            </div>

            {/* Fee breakdown + Balance after */}
            {numericAmount > 0 && !isInsufficient && FEE_CONFIG.payment.rate > 0 && (() => {
              const { fee, storeAmount } = FEE_CONFIG.payment.calculate(numericAmount);
              const percent = (FEE_CONFIG.payment.rate * 100).toFixed(1);
              return (
                <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2 space-y-1">
                  <div className="font-display text-xs font-semibold text-text-primary">
                    {t('scan.feeBreakdown')}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-body text-xs text-text-secondary">
                      {t('scan.paymentAmount')}
                    </span>
                    <span className="font-mono text-xs text-text-primary">
                      &yen;{numericAmount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-body text-xs text-text-secondary">
                      {t('scan.feePercent', { percent })}
                    </span>
                    <span className="font-mono text-xs text-text-primary">
                      -&yen;{fee.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-200 pt-1">
                    <span className="font-body text-xs font-medium text-text-primary">
                      {t('scan.storeReceives')}
                    </span>
                    <span className="font-mono text-xs font-semibold text-text-primary">
                      &yen;{storeAmount.toLocaleString()}
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* #1190 Phase 4: 残高 / 支払後残高 の表示 (YOKOSO 通貨表示 mode 時のみ)。
               従来 web3 gate で hidden だった箇所を yokoso feature flag に置換。 */}
            {yokosoEnabled && (isInsufficient ? (
              <p className="mt-3 text-center font-body text-sm text-red-500 font-medium">
                {t('scan.insufficient')}
              </p>
            ) : (
              <p className="mt-3 text-center font-body text-sm text-text-secondary">
                {t('scan.balanceAfter')}: &yen; {balanceAfter.toLocaleString()}
              </p>
            ))}

            {/* #1769 カードもポイントも使えない店 (KYC 未完了 / ポイントを断っている現金のみの店) */}
            {noInAppPayment && (
              <p className="mt-3 text-center font-body text-sm text-red-500 font-medium">
                {t('scan.pointsNotAccepted')}
              </p>
            )}

            {/* Confirm Button */}
            <button
              onClick={handleConfirm}
              disabled={numericAmount <= 0 || (yokosoEnabled && !hasStripe && isInsufficient) || showProcessing || noInAppPayment}
              className={`tap-effect mt-4 h-14 w-full rounded-xl font-display text-base font-semibold text-white transition-colors ${
                numericAmount > 0 && !(yokosoEnabled && !hasStripe && isInsufficient) && !showProcessing && !noInAppPayment
                  ? 'bg-yokoso-red shadow-button hover:bg-yokoso-red-dark'
                  : 'cursor-not-allowed bg-gray-300'
              }`}
            >
              {t('scan.confirm', { amount: numericAmount.toLocaleString() })}
            </button>
          </div>
        </div>
        </div>
        </>
      )}

      {/* Payment Method Choice Modal */}
      {showPaymentChoice && (
        <div className="fixed inset-0 z-[110] flex items-end bg-black/60" style={{ maxWidth: 430, margin: '0 auto' }}>
          <div className="animate-slide-up w-full rounded-t-3xl bg-white p-5 pb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold text-text-primary">
                {t('scan.choosePayment')}
              </h3>
              <button onClick={() => setShowPaymentChoice(false)} className="tap-effect p-1">
                <X size={20} className="text-text-tertiary" />
              </button>
            </div>

            <p className="font-body text-sm text-text-secondary mb-4">
              &yen;{numericAmount.toLocaleString()} — {storeName}
            </p>

            <div className="flex flex-col gap-3">
              {/* Credit Card option */}
              <button
                onClick={handlePayWithStripe}
                disabled={stripeLoading}
                className="tap-effect flex items-center gap-4 rounded-xl border-2 border-navy/20 bg-white p-4 transition-colors hover:border-navy/40 hover:bg-navy/5"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/10">
                  <CreditCard size={24} className="text-navy" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-display text-sm font-semibold text-text-primary">
                    {t('scan.payWithCard')}
                  </p>
                  <p className="font-body text-xs text-text-secondary">
                    {t('scan.payWithCardDesc')}
                  </p>
                </div>
              </button>

              {/* JPYC Balance option — #1190 Phase 4: yokoso mode では残高不足でも
                 tap 可能 (auto top-up sheet を出す)。従来 mode は disabled 維持。 */}
              <button
                onClick={handlePayWithBalance}
                disabled={isInsufficient && !yokosoEnabled}
                className={`tap-effect flex items-center gap-4 rounded-xl border-2 p-4 transition-colors ${
                  isInsufficient && !yokosoEnabled
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                    : 'border-matcha/20 bg-white hover:border-matcha/40 hover:bg-matcha/5'
                }`}
              >
                <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${
                  isInsufficient ? 'bg-gray-100' : 'bg-matcha/10'
                }`}>
                  <Wallet size={24} className={isInsufficient ? 'text-gray-400' : 'text-matcha'} />
                </div>
                <div className="flex-1 text-left">
                  <p className={`font-display text-sm font-semibold ${
                    isInsufficient ? 'text-text-tertiary' : 'text-text-primary'
                  }`}>
                    {/* #1190 Phase 4: 「YOKOSO 通貨で支払う」に呼称を統一 */}
                    {t('payment.methodYokoso')}
                  </p>
                  <p className={`font-body text-xs ${
                    isInsufficient ? 'text-red-400' : 'text-text-secondary'
                  }`}>
                    {/* Phase 4: 残高不足時は "自動チャージして支払う" を促す */}
                    {isInsufficient
                      ? t('autoTopUp.shortfall') + ' ¥' + Math.max(0, numericAmount - balance).toLocaleString()
                      : t('scan.balanceAvailable', { balance: balance.toLocaleString() })
                    }
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stripe Checkout Modal */}
      {stripeClientSecret && (
        <div className="fixed inset-0 z-[120] flex items-end bg-black/60" style={{ maxWidth: 430, margin: '0 auto' }}>
          <div className="animate-slide-up w-full rounded-t-3xl bg-white p-5 pb-8 max-h-[85vh] overflow-auto">
            <StripeCheckout
              clientSecret={stripeClientSecret}
              amount={numericAmount}
              storeName={storeName}
              connectedAccountId={stripeConnectedAccountId}
              paymentIntentId={stripePaymentIntentId || undefined}
              storeId={targetStore?.id}
              onSuccess={handleStripeSuccess}
              onCancel={handleStripeCancelOrBack}
            />
          </div>
        </div>
      )}

      {/* #1190 Phase 4: 残高不足時の auto top-up 承認 sheet (demo mode 限定) */}
      <AutoTopUpSheet
        open={showAutoTopUp}
        breakdown={
          showAutoTopUp && numericAmount > 0
            ? (() => {
                const topupAmount = Math.max(0, numericAmount - balance)
                const topupFee = calculateAutoTopupCardFee(topupAmount)
                return {
                  balance,
                  billAmount: numericAmount,
                  topupAmount,
                  topupFee,
                  totalCharged: topupAmount + topupFee,
                  balanceAfter: 0,
                  isTopUpNeeded: true,
                }
              })()
            : null
        }
        storeName={storeName}
        onCancel={() => setShowAutoTopUp(false)}
        onConfirm={handleAutoTopUpConfirm}
      />

      {/* #734: Passkey 未登録ユーザーの決済 trigger 時の誘導 modal */}
      <PasskeyEnrollmentModal
        open={passkeyModalOpen}
        enforced
        onClose={() => setPasskeyModalOpen(false)}
        onEnrolled={() => {
          markEnrolled()
          setPasskeyModalOpen(false)
          // 登録完了 → ユーザーが再度「支払う」を押すと今度は通る
        }}
      />
    </div>
  )
}
